export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  date: string;
  readTime: string;
  image: string;
  author: string;
  authorRole: string;
  tags: string[];
  content: BlogSection[];
  relatedSlugs: string[];
}

export interface BlogSection {
  type: "h2" | "h3" | "p" | "ul" | "ol" | "callout" | "table";
  content?: string;
  items?: string[];
  variant?: "info" | "warning" | "success";
  headers?: string[];
  rows?: string[][];
}

export const blogCategories = [
  { label: "Tous", slug: "tous" },
  { label: "DPE", slug: "dpe" },
  { label: "Vente", slug: "vente" },
  { label: "Location", slug: "location" },
  { label: "Réglementation", slug: "reglementation" },
  { label: "Tarifs", slug: "tarifs" },
  { label: "Local SEO", slug: "local" },
];

export const blogPosts: BlogPost[] = [
  {
    id: 1,
    slug: "dpe-paris-15e-arrondissement",
    title: "DPE Paris 15e : tout savoir sur le diagnostic énergétique dans le 15e arrondissement",
    excerpt: "Le 15e arrondissement de Paris concentre un parc immobilier varié. Découvrez les spécificités du DPE dans le 15e, les tarifs et comment obtenir votre diagnostic rapidement.",
    category: "DPE",
    categorySlug: "dpe",
    date: "10 avril 2025",
    readTime: "7 min",
    image: "/img/readdy/blog-dpe-15e.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["DPE", "Paris 15e", "Diagnostic immobilier", "Énergie"],
    relatedSlugs: ["dpe-2025-changements-paris", "cout-dpe-paris-2025", "diagnostics-obligatoires-vente-paris"],
    content: [
      { type: "p", content: "Le 15e arrondissement de Paris est l'un des plus peuplés de la capitale avec plus de 230 000 habitants. Son parc immobilier est particulièrement diversifié : immeubles haussmanniens du XIXe siècle, constructions des années 60-70 autour de Beaugrenelle, et résidences modernes. Cette diversité implique des besoins en DPE très variés selon l'époque de construction." },
      { type: "h2", content: "Pourquoi réaliser un DPE dans le 15e arrondissement ?" },
      { type: "p", content: "Le DPE (Diagnostic de Performance Énergétique) est obligatoire pour toute vente ou mise en location d'un bien immobilier dans le 15e arrondissement, comme partout en France. Depuis la réforme de juillet 2021, le DPE est devenu opposable juridiquement, ce qui renforce son importance dans les transactions immobilières parisiennes." },
      { type: "callout", variant: "warning", content: "Depuis le 1er janvier 2025, les logements classés G (consommation > 450 kWh/m²/an) sont interdits à la location dans tout Paris, y compris le 15e arrondissement. Si votre bien est classé G, vous devez réaliser des travaux de rénovation énergétique avant de le remettre en location." },
      { type: "h2", content: "Les spécificités du parc immobilier du 15e" },
      { type: "p", content: "Le 15e arrondissement présente plusieurs typologies de biens qui influencent directement les résultats du DPE :" },
      { type: "ul", items: [
        "Immeubles haussmanniens (1850-1920) : souvent classés E ou F en raison de l'isolation insuffisante des murs et des fenêtres simples vitrage",
        "Constructions des années 60-70 (secteur Beaugrenelle) : généralement classées D ou E, avec des systèmes de chauffage collectif",
        "Résidences récentes (après 2000) : classées A, B ou C grâce aux normes thermiques modernes (RT 2000, RT 2005, RT 2012)",
        "Maisons de ville : résultats très variables selon les travaux de rénovation réalisés",
      ]},
      { type: "h2", content: "Tarifs du DPE dans le 15e arrondissement" },
      { type: "p", content: "Chez Up Diag, nos tarifs pour un DPE dans le 15e arrondissement de Paris sont les suivants :" },
      { type: "table", headers: ["Type de bien", "Surface", "Tarif TTC"], rows: [
        ["Studio / T1", "Moins de 35 m²", "130 €"],
        ["T2 / T3", "35 à 80 m²", "150 €"],
        ["T4 et plus", "Plus de 80 m²", "180 €"],
        ["Maison individuelle", "Toute surface", "Sur devis"],
      ]},
      { type: "callout", variant: "info", content: "Le déplacement est inclus dans nos tarifs pour toute intervention dans le 15e arrondissement. Vous recevez votre rapport DPE certifié par email le jour même de la visite." },
      { type: "h2", content: "Comment se déroule un DPE dans le 15e ?" },
      { type: "ol", items: [
        "Prise de rendez-vous en ligne ou par téléphone — intervention sous 48h dans le 15e",
        "Visite du bien par notre diagnostiqueur certifié COFRAC (1h à 2h selon la surface)",
        "Inspection des équipements de chauffage, de production d'eau chaude, de ventilation et de l'isolation",
        "Calcul de la consommation énergétique selon la méthode 3CL-DPE 2021",
        "Envoi du rapport certifié par email le jour même, avec enregistrement sur le registre ADEME",
      ]},
      { type: "h2", content: "Améliorer son DPE dans le 15e : les travaux prioritaires" },
      { type: "p", content: "Si votre bien dans le 15e est classé E, F ou G, plusieurs travaux peuvent améliorer significativement votre étiquette énergétique :" },
      { type: "ul", items: [
        "Isolation des combles et des murs par l'intérieur (gain de 1 à 2 lettres)",
        "Remplacement des fenêtres simple vitrage par du double vitrage",
        "Installation d'une chaudière à condensation ou d'une pompe à chaleur",
        "Pose d'un thermostat programmable et régulation du chauffage",
        "Isolation du plancher bas si vide sanitaire ou cave",
      ]},
      { type: "callout", variant: "success", content: "Des aides financières existent pour financer vos travaux de rénovation énergétique : MaPrimeRénov', Éco-PTZ, CEE (Certificats d'Économies d'Énergie). Un DPE favorable peut augmenter la valeur de votre bien de 5 à 15% dans le 15e arrondissement." },
      { type: "h2", content: "Pourquoi choisir Up Diag pour votre DPE dans le 15e ?" },
      { type: "ul", items: [
        "Diagnostiqueurs certifiés COFRAC, spécialisés dans le parc immobilier parisien",
        "Intervention sous 48h dans tout le 15e arrondissement",
        "Rapport DPE certifié envoyé le jour même par email",
        "Tarifs transparents, déplacement inclus",
        "4.9/5 de satisfaction client sur Google",
      ]},
    ],
  },

  {
    id: 2,
    slug: "diagnostic-amiante-boulogne-billancourt",
    title: "Diagnostic Amiante à Boulogne-Billancourt : obligations, tarifs et procédure",
    excerpt: "Boulogne-Billancourt concentre de nombreux immeubles construits avant 1997. Découvrez tout ce qu'il faut savoir sur le diagnostic amiante dans cette commune des Hauts-de-Seine.",
    category: "Local SEO",
    categorySlug: "local",
    date: "5 avril 2025",
    readTime: "6 min",
    image: "/img/readdy/blog-amiante-boulogne.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Amiante", "Boulogne-Billancourt", "92", "Diagnostic immobilier"],
    relatedSlugs: ["diagnostics-obligatoires-vente-paris", "plomb-crep-paris-avant-1949", "dpe-paris-15e-arrondissement"],
    content: [
      { type: "p", content: "Boulogne-Billancourt est la commune la plus peuplée des Hauts-de-Seine avec plus de 120 000 habitants. Ville résidentielle prisée, elle concentre un parc immobilier très varié : immeubles des années 30-50 dans le quartier du Pont de Sèvres, constructions des années 60-80 dans le secteur de l'Île Seguin, et résidences récentes. Une grande partie de ce parc est concernée par le diagnostic amiante obligatoire." },
      { type: "h2", content: "Quels biens sont concernés par le diagnostic amiante à Boulogne-Billancourt ?" },
      { type: "p", content: "Le diagnostic amiante est obligatoire pour tous les biens dont le permis de construire a été délivré avant le 1er juillet 1997. À Boulogne-Billancourt, cela concerne une très large partie du parc immobilier, notamment :" },
      { type: "ul", items: [
        "Les immeubles des années 30-50 dans les quartiers anciens (Billancourt, Pont de Sèvres)",
        "Les constructions des années 60-70 dans le secteur de l'Île Seguin et du centre-ville",
        "Les immeubles des années 80 dans les quartiers résidentiels",
        "Les maisons individuelles construites avant 1997",
        "Les locaux commerciaux et bureaux construits avant 1997",
      ]},
      { type: "callout", variant: "warning", content: "Attention : même si votre bien a été entièrement rénové, le diagnostic amiante reste obligatoire si le permis de construire initial date d'avant le 1er juillet 1997. Des traces d'amiante peuvent subsister sous les nouvelles couches de peinture ou dans les matériaux de structure." },
      { type: "h2", content: "Où peut-on trouver de l'amiante dans un bien à Boulogne-Billancourt ?" },
      { type: "p", content: "L'amiante a été utilisé dans de nombreux matériaux de construction jusqu'en 1997. Dans les biens de Boulogne-Billancourt, on peut en trouver dans :" },
      { type: "ul", items: [
        "Les faux plafonds et les dalles de sol en vinyle-amiante",
        "Les calorifugeages des canalisations et des chaudières",
        "Les enduits et colles de carrelage",
        "Les plaques ondulées de toiture ou de bardage",
        "Les joints de portes de four et d'appareils de chauffage",
        "Les conduits de ventilation et de fumée",
      ]},
      { type: "h2", content: "Tarifs du diagnostic amiante à Boulogne-Billancourt" },
      { type: "table", headers: ["Type de bien", "Tarif TTC"], rows: [
        ["Appartement (toute surface)", "120 €"],
        ["Maison individuelle", "150 €"],
        ["Local commercial / professionnel", "Sur devis"],
        ["Parties communes d'immeuble", "Sur devis"],
      ]},
      { type: "callout", variant: "info", content: "Le déplacement est inclus dans nos tarifs pour toute intervention à Boulogne-Billancourt. Nous intervenons généralement sous 24 à 48h. Vous recevez votre rapport certifié par email le jour même." },
      { type: "h2", content: "Déroulement du diagnostic amiante à Boulogne-Billancourt" },
      { type: "ol", items: [
        "Prise de rendez-vous en ligne ou par téléphone — intervention sous 48h à Boulogne-Billancourt",
        "Inspection visuelle minutieuse de tous les matériaux susceptibles de contenir de l'amiante",
        "Prélèvements si nécessaire, envoyés en laboratoire accrédité COFRAC pour analyse",
        "Rédaction du rapport avec localisation des matériaux amiantés et état de conservation",
        "Envoi du rapport certifié par email le jour même",
      ]},
      { type: "h2", content: "Que faire si de l'amiante est détectée dans votre bien ?" },
      { type: "p", content: "Si notre diagnostiqueur détecte de l'amiante dans votre bien à Boulogne-Billancourt, plusieurs situations sont possibles :" },
      { type: "ul", items: [
        "Amiante en bon état de conservation : surveillance périodique recommandée tous les 3 ans, pas de travaux obligatoires immédiats",
        "Amiante dégradée ou friable : travaux de désamiantage obligatoires par une entreprise certifiée",
        "Amiante dans les parties communes : le syndic de copropriété doit être informé et le DTA (Dossier Technique Amiante) mis à jour",
      ]},
      { type: "callout", variant: "success", content: "Up Diag intervient dans toute la commune de Boulogne-Billancourt, du quartier du Pont de Sèvres au secteur de l'Île Seguin. Nos diagnostiqueurs connaissent parfaitement le parc immobilier boulonnais et ses spécificités." },
    ],
  },

  {
    id: 3,
    slug: "dpe-2025-changements-paris",
    title: "DPE 2025 : ce qui change pour les propriétaires parisiens",
    excerpt: "La réforme du DPE continue d'évoluer en 2025. Découvrez les nouvelles obligations pour les propriétaires à Paris et en Île-de-France, notamment l'interdiction des logements G.",
    category: "DPE",
    categorySlug: "dpe",
    date: "12 mars 2025",
    readTime: "8 min",
    image: "/img/readdy/b1.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["DPE", "Réglementation 2025", "Paris", "Logement G"],
    relatedSlugs: ["dpe-paris-15e-arrondissement", "cout-dpe-paris-2025", "diagnostics-obligatoires-vente-paris"],
    content: [
      { type: "p", content: "L'année 2025 marque un tournant majeur pour les propriétaires parisiens avec l'entrée en vigueur de nouvelles obligations liées au DPE. La loi Climat et Résilience de 2021 continue de déployer ses effets, avec des conséquences concrètes pour les bailleurs et les vendeurs en Île-de-France." },
      { type: "h2", content: "L'interdiction des logements G à la location depuis janvier 2025" },
      { type: "p", content: "Depuis le 1er janvier 2025, les logements classés G (consommation énergétique supérieure à 450 kWh/m²/an) sont interdits à la location dans toute la France, y compris à Paris et en Île-de-France. Cette mesure concerne environ 600 000 logements en France, dont une part significative à Paris." },
      { type: "callout", variant: "warning", content: "Si votre logement est classé G et que vous souhaitez le louer, vous devez impérativement réaliser des travaux de rénovation énergétique pour améliorer son classement avant de le remettre sur le marché locatif. Un nouveau DPE sera nécessaire après les travaux." },
      { type: "h2", content: "Le calendrier des interdictions à venir" },
      { type: "table", headers: ["Date", "Interdiction", "Classement concerné"], rows: [
        ["1er janvier 2025", "Interdiction de location", "Logements G (> 450 kWh/m²/an)"],
        ["1er janvier 2028", "Interdiction de location", "Logements F"],
        ["1er janvier 2034", "Interdiction de location", "Logements E"],
      ]},
      { type: "h2", content: "Les DPE expirés : attention aux dates limites" },
      { type: "p", content: "Depuis le 1er janvier 2025, tous les DPE réalisés entre le 1er janvier 2018 et le 30 juin 2021 sont expirés. Si votre DPE a été réalisé pendant cette période, vous devez en faire réaliser un nouveau avant toute vente ou mise en location." },
      { type: "ul", items: [
        "DPE réalisés avant le 1er janvier 2018 : expirés depuis le 1er janvier 2023",
        "DPE réalisés entre le 1er janvier 2018 et le 30 juin 2021 : expirés depuis le 31 décembre 2024",
        "DPE réalisés après le 1er juillet 2021 : valables 10 ans",
      ]},
      { type: "h2", content: "Le DPE collectif obligatoire pour les copropriétés" },
      { type: "p", content: "Depuis le 1er janvier 2024, les copropriétés de plus de 200 lots doivent disposer d'un DPE collectif. En 2025, cette obligation s'étend aux copropriétés de 50 à 200 lots. À Paris, où les copropriétés sont très nombreuses, cette mesure concerne des milliers d'immeubles." },
      { type: "callout", variant: "info", content: "Up Diag réalise des DPE collectifs pour les copropriétés parisiennes. Contactez-nous pour un devis adapté à la taille de votre immeuble." },
      { type: "h2", content: "Comment se préparer en 2025 ?" },
      { type: "ul", items: [
        "Vérifiez la date de votre DPE actuel et sa validité",
        "Si votre logement est classé F ou G, anticipez les travaux de rénovation",
        "Profitez des aides financières disponibles : MaPrimeRénov', Éco-PTZ, CEE",
        "Faites réaliser un audit énergétique pour identifier les travaux prioritaires",
        "Contactez Up Diag pour un nouveau DPE sous 48h",
      ]},
    ],
  },

  {
    id: 4,
    slug: "diagnostics-obligatoires-vente-paris",
    title: "Quels diagnostics obligatoires pour vendre à Paris ?",
    excerpt: "Avant de mettre votre bien en vente à Paris, vous devez constituer un dossier de diagnostic technique complet. Voici tout ce qu'il faut savoir sur les diagnostics obligatoires.",
    category: "Vente",
    categorySlug: "vente",
    date: "5 mars 2025",
    readTime: "6 min",
    image: "/img/readdy/b2.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Vente", "DDT", "Paris", "Diagnostics obligatoires"],
    relatedSlugs: ["dpe-2025-changements-paris", "cout-dpe-paris-2025", "dpe-paris-15e-arrondissement"],
    content: [
      { type: "p", content: "La vente d'un bien immobilier à Paris implique la constitution d'un Dossier de Diagnostic Technique (DDT) complet. Ce dossier doit être annexé à la promesse de vente et à l'acte authentique. Il protège l'acheteur en l'informant sur l'état du bien et dégage la responsabilité du vendeur en cas de vices cachés liés aux éléments diagnostiqués." },
      { type: "h2", content: "Les diagnostics obligatoires pour toute vente à Paris" },
      { type: "ul", items: [
        "DPE (Diagnostic de Performance Énergétique) : obligatoire pour tous les biens, validité 10 ans",
        "État des Risques et Pollutions (ERP) : obligatoire pour tous les biens, validité 6 mois",
        "Diagnostic Amiante : obligatoire si le permis de construire date d'avant le 1er juillet 1997",
        "CREP Plomb : obligatoire si le bien a été construit avant le 1er janvier 1949",
        "Diagnostic Électricité : obligatoire si l'installation a plus de 15 ans",
        "Diagnostic Gaz : obligatoire si l'installation a plus de 15 ans",
        "Mesurage Loi Carrez : obligatoire pour tout lot en copropriété",
        "Diagnostic Termites : obligatoire dans les zones à risque définies par arrêté préfectoral",
      ]},
      { type: "callout", variant: "info", content: "À Paris, la quasi-totalité des appartements en copropriété nécessitent le mesurage Loi Carrez. De plus, les immeubles haussmanniens (construits avant 1920) nécessitent systématiquement le CREP Plomb et le diagnostic Amiante." },
      { type: "h2", content: "Durée de validité des diagnostics" },
      { type: "table", headers: ["Diagnostic", "Durée de validité"], rows: [
        ["DPE", "10 ans"],
        ["Amiante (absence)", "Illimitée"],
        ["Amiante (présence)", "3 ans"],
        ["Plomb / CREP (absence)", "Illimitée"],
        ["Plomb / CREP (présence)", "1 an"],
        ["Électricité", "3 ans"],
        ["Gaz", "3 ans"],
        ["Loi Carrez", "Illimitée (sans travaux)"],
        ["ERP", "6 mois"],
        ["Termites", "6 mois"],
      ]},
      { type: "h2", content: "Notre Pack Vente : tous vos diagnostics en un seul rendez-vous" },
      { type: "p", content: "Chez Up Diag, notre Pack Vente regroupe l'ensemble des diagnostics obligatoires pour la vente de votre bien parisien. Un seul rendez-vous de 2h à 3h suffit pour réaliser tous les diagnostics nécessaires. Vous recevez l'ensemble de vos rapports certifiés par email le jour même." },
      { type: "callout", variant: "success", content: "Notre Pack Vente est disponible dès 280 € TTC pour un appartement jusqu'à 50 m², déplacement inclus dans tout Paris. Devis gratuit en 2 minutes sur notre site." },
    ],
  },

  {
    id: 5,
    slug: "cout-dpe-paris-2025",
    title: "Combien coûte un DPE à Paris en 2025 ?",
    excerpt: "Le prix d'un DPE à Paris varie selon la surface et le type de bien. Découvrez nos tarifs transparents et ce qui est inclus dans notre prestation de diagnostic énergétique.",
    category: "Tarifs",
    categorySlug: "tarifs",
    date: "28 février 2025",
    readTime: "5 min",
    image: "/img/readdy/b3.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["DPE", "Tarifs", "Paris", "Prix diagnostic"],
    relatedSlugs: ["dpe-2025-changements-paris", "dpe-paris-15e-arrondissement", "diagnostics-obligatoires-vente-paris"],
    content: [
      { type: "p", content: "Le prix d'un DPE à Paris dépend principalement de la surface du bien et de son type (appartement, maison, local commercial). Contrairement à une idée reçue, le DPE n'est pas réglementé en termes de tarif : chaque diagnostiqueur fixe librement ses prix. Il est donc important de comparer les offres et de vérifier les certifications." },
      { type: "h2", content: "Nos tarifs DPE à Paris en 2025" },
      { type: "table", headers: ["Type de bien", "Surface", "Tarif Up Diag"], rows: [
        ["Studio / T1", "Moins de 35 m²", "130 € TTC"],
        ["T2 / T3", "35 à 80 m²", "150 € TTC"],
        ["T4 et plus", "Plus de 80 m²", "180 € TTC"],
        ["Maison individuelle", "Toute surface", "Sur devis"],
        ["Local commercial", "Toute surface", "Sur devis"],
      ]},
      { type: "callout", variant: "info", content: "Tous nos tarifs incluent le déplacement dans Paris et la petite couronne, la réalisation du DPE par un diagnostiqueur certifié COFRAC, et l'envoi du rapport certifié par email le jour même." },
      { type: "h2", content: "Qu'est-ce qui est inclus dans notre DPE ?" },
      { type: "ul", items: [
        "Déplacement inclus dans tout Paris (75) et la petite couronne (92, 93, 94)",
        "Visite du bien par un diagnostiqueur certifié COFRAC",
        "Inspection complète : chauffage, eau chaude, ventilation, isolation, menuiseries",
        "Calcul selon la méthode 3CL-DPE 2021 (méthode officielle)",
        "Rapport DPE certifié avec étiquettes énergie et climat",
        "Enregistrement sur le registre national ADEME",
        "Envoi du rapport par email le jour même",
      ]},
      { type: "h2", content: "Méfiez-vous des DPE trop bon marché" },
      { type: "p", content: "Depuis que le DPE est devenu opposable juridiquement en 2021, un DPE erroné peut engager la responsabilité du vendeur. Il est donc crucial de choisir un diagnostiqueur certifié et sérieux. Méfiez-vous des offres à moins de 80 € qui peuvent cacher des diagnostiqueurs non certifiés ou des rapports bâclés." },
      { type: "callout", variant: "warning", content: "Un DPE réalisé par un diagnostiqueur non certifié n'a aucune valeur légale et ne peut pas être utilisé pour une vente ou une location. Vérifiez toujours les certifications de votre diagnostiqueur sur le site officiel de l'ADEME." },
    ],
  },

  {
    id: 6,
    slug: "plomb-crep-paris-avant-1949",
    title: "Diagnostic Plomb (CREP) à Paris : tout savoir pour les biens avant 1949",
    excerpt: "Paris concentre un parc immobilier exceptionnel avec de nombreux biens construits avant 1949. Le CREP est obligatoire pour ces biens. Découvrez les obligations, tarifs et procédure.",
    category: "Réglementation",
    categorySlug: "reglementation",
    date: "20 février 2025",
    readTime: "6 min",
    image: "/img/readdy/blog-plomb-paris.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Plomb", "CREP", "Paris", "Avant 1949", "Haussmann"],
    relatedSlugs: ["diagnostics-obligatoires-vente-paris", "diagnostic-amiante-boulogne-billancourt", "dpe-paris-15e-arrondissement"],
    content: [
      { type: "p", content: "Paris est une ville d'exception avec un patrimoine architectural unique. Plus de 60% des logements parisiens ont été construits avant 1948, ce qui en fait l'une des villes françaises les plus concernées par le diagnostic Plomb (CREP). Les immeubles haussmanniens, construits entre 1850 et 1920, représentent à eux seuls une part majeure du parc immobilier parisien." },
      { type: "h2", content: "Pourquoi le plomb est-il dangereux ?" },
      { type: "p", content: "Le plomb a été largement utilisé dans les peintures jusqu'aux années 1940-1950. Il est particulièrement dangereux pour les enfants en bas âge qui peuvent ingérer des écailles de peinture au plomb (saturnisme). Chez les adultes, une exposition prolongée peut provoquer des troubles neurologiques, rénaux et cardiovasculaires." },
      { type: "callout", variant: "warning", content: "Le saturnisme infantile est une maladie grave causée par l'ingestion de plomb. En France, environ 85 000 enfants sont exposés au risque de saturnisme, principalement dans les logements anciens parisiens. Le CREP permet d'identifier et de traiter les sources d'exposition." },
      { type: "h2", content: "Quels biens parisiens sont concernés par le CREP ?" },
      { type: "ul", items: [
        "Tous les logements construits avant le 1er janvier 1949 (vente ET location)",
        "Les immeubles haussmanniens (1850-1920) : systématiquement concernés",
        "Les immeubles de l'entre-deux-guerres (1920-1940)",
        "Les constructions de l'immédiat après-guerre (1945-1948)",
        "Les parties communes des immeubles avant 1949",
      ]},
      { type: "h2", content: "Tarifs du CREP à Paris" },
      { type: "table", headers: ["Type de bien", "Surface", "Tarif TTC"], rows: [
        ["Appartement", "Jusqu'à 50 m²", "110 €"],
        ["Appartement", "50 à 100 m²", "130 €"],
        ["Maison individuelle", "Toute surface", "150 €"],
        ["Parties communes", "Sur devis", "Sur devis"],
      ]},
      { type: "h2", content: "La technique de mesure : la fluorescence X" },
      { type: "p", content: "Le CREP est réalisé par fluorescence X, une technique non destructive qui permet de mesurer instantanément la teneur en plomb de chaque revêtement. Notre diagnostiqueur passe l'appareil sur chaque surface (murs, plafonds, boiseries, radiateurs) et obtient immédiatement le résultat en mg/cm²." },
      { type: "callout", variant: "success", content: "La mesure par fluorescence X est rapide, précise et non destructive. Elle ne nécessite aucun prélèvement ni dégradation des revêtements. Vous obtenez les résultats le jour même." },
    ],
  },

  {
    id: 7,
    slug: "dpe-vincennes-val-de-marne",
    title: "DPE à Vincennes et dans le Val-de-Marne : guide complet 2025",
    excerpt: "Vincennes et les communes du Val-de-Marne présentent un parc immobilier varié. Découvrez les spécificités du DPE dans le 94 et comment obtenir votre diagnostic rapidement.",
    category: "Local SEO",
    categorySlug: "local",
    date: "15 février 2025",
    readTime: "6 min",
    image: "/img/readdy/blog-dpe-vincennes.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["DPE", "Vincennes", "Val-de-Marne", "94"],
    relatedSlugs: ["dpe-2025-changements-paris", "diagnostics-obligatoires-vente-paris", "cout-dpe-paris-2025"],
    content: [
      { type: "p", content: "Vincennes est l'une des communes les plus prisées du Val-de-Marne, avec un marché immobilier très actif. Limitrophe de Paris, elle attire de nombreux acheteurs et locataires en quête d'un cadre de vie agréable à proximité de la capitale. Le DPE y est particulièrement important en raison de la diversité du parc immobilier." },
      { type: "h2", content: "Le parc immobilier de Vincennes et du Val-de-Marne" },
      { type: "p", content: "Vincennes et les communes voisines du Val-de-Marne présentent un parc immobilier très varié :" },
      { type: "ul", items: [
        "Maisons individuelles des années 20-50 dans les quartiers résidentiels (souvent classées E ou F)",
        "Immeubles haussmanniens et de l'entre-deux-guerres (classés D à F)",
        "Constructions des années 60-80 (classées D ou E)",
        "Résidences récentes (classées A, B ou C)",
      ]},
      { type: "callout", variant: "info", content: "À Vincennes, les maisons individuelles représentent une part importante du parc. Ces biens, souvent construits avant 1950, sont fréquemment classés E ou F en raison de leur isolation insuffisante. Le DPE est crucial pour valoriser ces biens ou anticiper des travaux de rénovation." },
      { type: "h2", content: "Nos tarifs DPE dans le Val-de-Marne" },
      { type: "table", headers: ["Type de bien", "Surface", "Tarif TTC"], rows: [
        ["Studio / T1", "Moins de 35 m²", "130 €"],
        ["T2 / T3", "35 à 80 m²", "150 €"],
        ["T4 et plus", "Plus de 80 m²", "180 €"],
        ["Maison individuelle", "Toute surface", "Sur devis"],
      ]},
      { type: "h2", content: "Intervention rapide dans tout le Val-de-Marne" },
      { type: "p", content: "Up Diag intervient dans toutes les communes du Val-de-Marne sous 48h : Vincennes, Créteil, Vitry-sur-Seine, Ivry-sur-Seine, Saint-Maur-des-Fossés, Champigny-sur-Marne, Maisons-Alfort, Charenton-le-Pont, Nogent-sur-Marne, Fontenay-sous-Bois et toutes les autres communes du département." },
      { type: "callout", variant: "success", content: "Devis gratuit en 2 minutes sur notre site. Intervention sous 48h à Vincennes et dans tout le Val-de-Marne. Rapport DPE certifié envoyé le jour même par email." },
    ],
  },

  {
    id: 8,
    slug: "diagnostics-location-obligations-2025",
    title: "Diagnostics obligatoires pour la location en 2025 : ce que tout bailleur doit savoir",
    excerpt: "Les obligations diagnostics pour la location se sont renforcées ces dernières années. Découvrez la liste complète des diagnostics requis pour louer votre bien en 2025.",
    category: "Location",
    categorySlug: "location",
    date: "10 février 2025",
    readTime: "7 min",
    image: "/img/readdy/blog-location-2025.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Location", "Diagnostics obligatoires", "Bailleur", "2025"],
    relatedSlugs: ["dpe-2025-changements-paris", "diagnostics-obligatoires-vente-paris", "cout-dpe-paris-2025"],
    content: [
      { type: "p", content: "En 2025, les obligations diagnostics pour la location immobilière se sont considérablement renforcées. Entre l'interdiction des logements G, les nouvelles exigences du DPE et les diagnostics techniques obligatoires, les bailleurs doivent être particulièrement vigilants pour rester en conformité avec la réglementation." },
      { type: "h2", content: "La liste complète des diagnostics obligatoires pour la location en 2025" },
      { type: "ul", items: [
        "DPE (Diagnostic de Performance Énergétique) : obligatoire pour tous les logements, validité 10 ans",
        "CREP Plomb : obligatoire pour les logements construits avant le 1er janvier 1949, validité 6 ans",
        "Diagnostic Électricité : obligatoire pour les installations de plus de 15 ans, validité 6 ans",
        "Diagnostic Gaz : obligatoire pour les installations de plus de 15 ans, validité 6 ans",
        "État des Risques et Pollutions (ERP) : obligatoire pour tous les logements, validité 6 mois",
        "Diagnostic Bruit : obligatoire dans les zones exposées au bruit aérien (PEB)",
      ]},
      { type: "callout", variant: "warning", content: "Depuis le 1er janvier 2025, les logements classés G sont interdits à la location. Si votre bien est classé G, vous ne pouvez plus le louer sans réaliser des travaux de rénovation énergétique. Un nouveau DPE sera nécessaire après les travaux pour prouver l'amélioration." },
      { type: "h2", content: "Les sanctions en cas de non-respect des obligations" },
      { type: "p", content: "Un bailleur qui ne fournit pas les diagnostics obligatoires s'expose à plusieurs sanctions :" },
      { type: "ul", items: [
        "Impossibilité d'augmenter le loyer en cours de bail",
        "Résiliation du bail par le locataire sans préavis",
        "Poursuites judiciaires et dommages et intérêts",
        "Amende pouvant aller jusqu'à 3 000 € pour une personne physique",
      ]},
      { type: "h2", content: "Notre Pack Location : tous vos diagnostics en un seul rendez-vous" },
      { type: "p", content: "Chez Up Diag, notre Pack Location regroupe l'ensemble des diagnostics obligatoires pour la mise en location de votre bien. Un seul rendez-vous de 1h30 à 2h suffit pour réaliser tous les diagnostics nécessaires." },
      { type: "table", headers: ["Surface", "Tarif Pack Location"], rows: [
        ["Studio / T1 (moins de 35 m²)", "180 € TTC"],
        ["T2 / T3 (35 à 80 m²)", "220 € TTC"],
        ["T4 et plus (plus de 80 m²)", "260 € TTC"],
      ]},
      { type: "callout", variant: "success", content: "Devis gratuit en 2 minutes. Intervention sous 48h dans tout Paris et l'Île-de-France. Rapport certifié envoyé le jour même." },
    ],
  },

  {
    id: 9,
    slug: "dpe-versailles-yvelines",
    title: "DPE à Versailles et dans les Yvelines : guide complet pour propriétaires",
    excerpt: "Versailles et les Yvelines concentrent un patrimoine immobilier exceptionnel. Découvrez les spécificités du DPE dans le 78 et comment valoriser votre bien grâce au diagnostic énergétique.",
    category: "Local SEO",
    categorySlug: "local",
    date: "5 février 2025",
    readTime: "6 min",
    image: "/img/readdy/blog-dpe-versailles.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["DPE", "Versailles", "Yvelines", "78"],
    relatedSlugs: ["dpe-2025-changements-paris", "diagnostics-obligatoires-vente-paris", "cout-dpe-paris-2025"],
    content: [
      { type: "p", content: "Versailles est l'une des villes les plus prestigieuses d'Île-de-France, avec un marché immobilier très actif et des prix parmi les plus élevés des Yvelines. Son patrimoine architectural exceptionnel — hôtels particuliers, maisons bourgeoises, immeubles du XIXe siècle — implique des besoins spécifiques en matière de diagnostics immobiliers, notamment le DPE." },
      { type: "h2", content: "Les spécificités du parc immobilier versaillais" },
      { type: "ul", items: [
        "Hôtels particuliers et maisons bourgeoises du XVIIIe-XIXe siècle : souvent classés E ou F",
        "Immeubles de l'entre-deux-guerres : classés D à F selon les travaux réalisés",
        "Pavillons des années 50-70 dans les quartiers résidentiels : classés D ou E",
        "Résidences récentes autour des gares RER : classées A, B ou C",
      ]},
      { type: "callout", variant: "info", content: "À Versailles, la valeur verte d'un bien est particulièrement importante. Un logement classé A ou B peut se vendre jusqu'à 15% plus cher qu'un logement équivalent classé E ou F. Le DPE est donc un outil stratégique pour valoriser votre patrimoine versaillais." },
      { type: "h2", content: "Intervention dans tout le département des Yvelines" },
      { type: "p", content: "Up Diag intervient dans toutes les communes des Yvelines : Versailles, Saint-Germain-en-Laye, Mantes-la-Jolie, Poissy, Sartrouville, Houilles, Chatou, Rambouillet, Guyancourt, Vélizy-Villacoublay et toutes les autres communes du département." },
      { type: "callout", variant: "success", content: "Devis gratuit en 2 minutes. Intervention sous 48h à Versailles et dans tout le 78. Rapport DPE certifié envoyé le jour même par email. Dès 130 € TTC, déplacement inclus." },
    ],
  },

  {
    id: 10,
    slug: "amiante-immeuble-annees-70-idf",
    title: "Diagnostic Amiante dans les immeubles des années 70 en Île-de-France",
    excerpt: "Les immeubles construits dans les années 70 en Île-de-France sont particulièrement concernés par le diagnostic amiante. Découvrez pourquoi et comment procéder.",
    category: "Réglementation",
    categorySlug: "reglementation",
    date: "28 janvier 2025",
    readTime: "7 min",
    image: "/img/readdy/blog-amiante-70s.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Amiante", "Années 70", "Île-de-France", "Diagnostic immobilier"],
    relatedSlugs: ["diagnostic-amiante-boulogne-billancourt", "diagnostics-obligatoires-vente-paris", "dpe-2025-changements-paris"],
    content: [
      { type: "p", content: "Les années 70 ont été une période de construction intensive en Île-de-France, avec la création de nombreuses villes nouvelles (Cergy-Pontoise, Marne-la-Vallée, Saint-Quentin-en-Yvelines) et de grands ensembles résidentiels. Ces constructions ont massivement utilisé l'amiante, qui était alors considéré comme un matériau miracle pour ses propriétés isolantes et ignifuges." },
      { type: "h2", content: "Pourquoi les immeubles des années 70 sont-ils particulièrement concernés ?" },
      { type: "p", content: "L'utilisation de l'amiante a atteint son pic dans les années 70 en France. Les immeubles construits pendant cette décennie contiennent souvent de l'amiante dans de nombreux matériaux :" },
      { type: "ul", items: [
        "Dalles de sol en vinyle-amiante (très répandues dans les appartements des années 70)",
        "Faux plafonds en plaques de fibres-ciment amiantées",
        "Calorifugeages des canalisations et des colonnes montantes",
        "Enduits projetés sur les structures métalliques (protection incendie)",
        "Joints et colles de carrelage",
        "Conduits de ventilation et de fumée",
      ]},
      { type: "callout", variant: "warning", content: "Dans les immeubles des années 70, l'amiante peut être présent dans des matériaux apparemment anodins comme les dalles de sol ou les faux plafonds. Seul un diagnostiqueur certifié peut identifier avec certitude la présence d'amiante." },
      { type: "h2", content: "Les villes nouvelles d'IDF particulièrement concernées" },
      { type: "p", content: "Les villes nouvelles créées dans les années 70 en Île-de-France concentrent une grande partie des immeubles concernés par le diagnostic amiante :" },
      { type: "ul", items: [
        "Cergy-Pontoise (Val-d'Oise) : nombreuses tours et barres des années 70-80",
        "Marne-la-Vallée (Seine-et-Marne) : Torcy, Lognes, Noisiel — constructions des années 70-80",
        "Saint-Quentin-en-Yvelines (Yvelines) : Guyancourt, Élancourt, Trappes",
        "Évry-Courcouronnes (Essonne) : grands ensembles des années 70",
        "Melun-Sénart (Seine-et-Marne) : Savigny-le-Temple, Combs-la-Ville",
      ]},
      { type: "callout", variant: "success", content: "Up Diag intervient dans toutes ces communes sous 48h. Nos diagnostiqueurs sont spécialisés dans les immeubles des années 70 et connaissent parfaitement les matériaux amiantés de cette époque." },
    ],
  },

  {
    id: 11,
    slug: "pack-vente-neuilly-sur-seine",
    title: "Pack Vente à Neuilly-sur-Seine : diagnostics immobiliers pour votre transaction",
    excerpt: "Neuilly-sur-Seine est l'une des communes les plus chères d'Île-de-France. Découvrez comment préparer votre dossier de diagnostic technique pour vendre dans les meilleures conditions.",
    category: "Local SEO",
    categorySlug: "local",
    date: "20 janvier 2025",
    readTime: "5 min",
    image: "/img/readdy/blog-neuilly.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Pack Vente", "Neuilly-sur-Seine", "92", "Diagnostics immobiliers"],
    relatedSlugs: ["diagnostics-obligatoires-vente-paris", "diagnostic-amiante-boulogne-billancourt", "cout-dpe-paris-2025"],
    content: [
      { type: "p", content: "Neuilly-sur-Seine est la commune la plus chère d'Île-de-France après Paris, avec des prix immobiliers parmi les plus élevés de France. Dans ce marché premium, la qualité du dossier de diagnostic technique est cruciale pour rassurer les acheteurs et sécuriser la transaction." },
      { type: "h2", content: "Les diagnostics indispensables pour vendre à Neuilly-sur-Seine" },
      { type: "p", content: "Le parc immobilier de Neuilly-sur-Seine est dominé par des immeubles de standing construits entre les années 30 et les années 80. La plupart des biens nécessitent un DDT complet comprenant :" },
      { type: "ul", items: [
        "DPE : obligatoire pour tous les biens, crucial pour la valeur verte",
        "Diagnostic Amiante : obligatoire pour les biens avant 1997 (très nombreux à Neuilly)",
        "CREP Plomb : obligatoire pour les biens avant 1949",
        "Diagnostic Électricité : obligatoire pour les installations de plus de 15 ans",
        "Diagnostic Gaz : obligatoire pour les installations de plus de 15 ans",
        "Mesurage Loi Carrez : obligatoire pour tous les appartements en copropriété",
      ]},
      { type: "callout", variant: "info", content: "À Neuilly-sur-Seine, un DPE favorable (A, B ou C) peut significativement valoriser votre bien. Dans ce marché premium, les acheteurs sont particulièrement attentifs à la performance énergétique et aux charges prévisionnelles." },
      { type: "h2", content: "Notre Pack Vente à Neuilly-sur-Seine" },
      { type: "p", content: "Chez Up Diag, notre Pack Vente regroupe tous les diagnostics obligatoires en un seul rendez-vous. Nous intervenons généralement sous 24h à Neuilly-sur-Seine, avec un rapport complet envoyé le jour même." },
      { type: "table", headers: ["Surface", "Tarif Pack Vente"], rows: [
        ["Appartement jusqu'à 50 m²", "280 € TTC"],
        ["Appartement 50 à 100 m²", "320 € TTC"],
        ["Appartement plus de 100 m²", "Sur devis"],
      ]},
      { type: "callout", variant: "success", content: "Devis gratuit en 2 minutes. Intervention sous 24h à Neuilly-sur-Seine. Rapport certifié envoyé le jour même, accepté par tous les notaires." },
    ],
  },

  {
    id: 12,
    slug: "electricite-gaz-diagnostic-paris-obligations",
    title: "Diagnostics Électricité et Gaz à Paris : obligations et tarifs 2025",
    excerpt: "Les diagnostics électricité et gaz sont obligatoires pour les installations de plus de 15 ans. Découvrez tout ce qu'il faut savoir pour vendre ou louer votre bien parisien en conformité.",
    category: "Réglementation",
    categorySlug: "reglementation",
    date: "15 janvier 2025",
    readTime: "6 min",
    image: "/img/readdy/blog-elec-gaz.jpg",
    author: "Équipe Up Diag",
    authorRole: "Diagnostiqueurs certifiés COFRAC",
    tags: ["Électricité", "Gaz", "Paris", "Diagnostics obligatoires"],
    relatedSlugs: ["diagnostics-obligatoires-vente-paris", "diagnostics-location-obligations-2025", "cout-dpe-paris-2025"],
    content: [
      { type: "p", content: "À Paris, de nombreux logements disposent d'installations électriques et de gaz vieillissantes. Les immeubles haussmanniens, les constructions des années 50-70 et même certaines résidences plus récentes peuvent avoir des installations de plus de 15 ans nécessitant un diagnostic obligatoire." },
      { type: "h2", content: "Quand les diagnostics électricité et gaz sont-ils obligatoires ?" },
      { type: "ul", items: [
        "Vente : obligatoires si l'installation électrique ou gaz a plus de 15 ans",
        "Location : obligatoires depuis 2018 si l'installation a plus de 15 ans",
        "Validité : 3 ans pour la vente, 6 ans pour la location",
      ]},
      { type: "callout", variant: "info", content: "À Paris, la grande majorité des logements haussmanniens et des constructions d'avant 1990 ont des installations électriques et gaz de plus de 15 ans. Ces diagnostics sont donc très fréquemment requis dans la capitale." },
      { type: "h2", content: "Que vérifie le diagnostic électricité ?" },
      { type: "ul", items: [
        "Le tableau électrique : disjoncteurs, fusibles, dispositifs différentiels",
        "La mise à la terre et les liaisons équipotentielles dans les pièces humides",
        "L'état des prises, interrupteurs et câblages apparents",
        "L'absence de matériels vétustes ou dangereux",
        "La conformité aux normes NF C 16-600",
      ]},
      { type: "h2", content: "Que vérifie le diagnostic gaz ?" },
      { type: "ul", items: [
        "L'état de la chaudière, du chauffe-eau et de la cuisinière",
        "Les tuyauteries, robinets et raccords",
        "La ventilation des appareils à gaz",
        "L'absence de fuites",
        "La classification des anomalies : A1 (sans danger), A2 (risque), DGI (danger grave immédiat)",
      ]},
      { type: "h2", content: "Tarifs des diagnostics électricité et gaz à Paris" },
      { type: "table", headers: ["Type de bien", "Électricité", "Gaz", "Les deux"], rows: [
        ["Studio / T1", "100 €", "100 €", "180 €"],
        ["T2 / T3", "120 €", "120 €", "210 €"],
        ["T4 et plus", "140 €", "140 €", "250 €"],
      ]},
      { type: "callout", variant: "success", content: "Économisez en combinant les diagnostics électricité et gaz lors d'une même visite. Devis gratuit en 2 minutes, intervention sous 48h dans tout Paris." },
    ],
  },
];
